import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { DEV_URL, rpost, setTokenGetter } from "utils/requests";
import DataHubLogo from "@enerex/unified-ui-react/logos/DataHubLogo";
import FormInput from "components/FormInput/index";

const Signup = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [companyName, setCompanyName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accountExistsLoginUrl, setAccountExistsLoginUrl] = useState("");
  const [success, setSuccess] = useState("");

  // Public page: provide a safe token getter for shared request helper.
  useEffect(() => {
    setTokenGetter(() => "");
  }, []);

  const canSubmit = useMemo(() => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      emailAddress.trim().length > 0 &&
      companyName.trim().length > 0
    );
  }, [firstName, lastName, emailAddress, companyName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setAccountExistsLoginUrl("");

    if (!canSubmit) {
      setError("Please fill in all fields.");
      return;
    }

    const emailToVerify = emailAddress.trim();

    setLoading(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailAddress: emailToVerify,
        companyName: companyName.trim(),
      };

      const res = await rpost(`${DEV_URL}/api/client/users/signup`, payload);
      const status = res?.status;

      if (!res || typeof status !== "number" || status < 200 || status >= 300) {
        // Backend might return a plain string in BadRequest(...).
        const rawMessage =
          typeof res?.data === "string"
            ? res.data
            : res?.data?.message || res?.data?.error || "Signup failed.";

        const isDomainNotFoundReview =
          status === 400 &&
          typeof rawMessage === "string" &&
          rawMessage.toLowerCase().includes("company domain not found");

        const isUserAddedInactive =
          status === 400 &&
          typeof rawMessage === "string" &&
          rawMessage.toLowerCase().includes("the company already has a paid subscription");

        const isUserAlreadyExists =
          status === 400 &&
          typeof rawMessage === "string" &&
          rawMessage.toLowerCase().includes("user already exist in our system");

        const isAccountAlreadyExists =
          status === 400 &&
          typeof res?.data === "object" &&
          res?.data &&
          (res.data.message === "Account Already Exists" ||
            (typeof rawMessage === "string" &&
              rawMessage.toLowerCase().includes("account already exists")));

        const loginUrlFromResponse =
          isAccountAlreadyExists && typeof res?.data?.loginUrl === "string"
            ? res.data.loginUrl
            : null;

        const message = isDomainNotFoundReview
          ? "We encountered an issue while processing your signup request and were unable to complete it at this time. Our sales team has been notified and will review your request."
          : isUserAddedInactive
            ? "The company already has a paid subscription. Please contact your organization’s admin to activate your user account."
            : isUserAlreadyExists
              ? "Account Already Exists."
              : rawMessage;
        if (isAccountAlreadyExists && loginUrlFromResponse) {
          setAccountExistsLoginUrl(loginUrlFromResponse);
        }
        setError(message);
        setFirstName("");
        setLastName("");
        setEmailAddress("");
        setCompanyName("");
        return;
      }

      setSuccess("Registration submitted successfully.");
      setFirstName("");
      setLastName("");
      setEmailAddress("");
      setCompanyName("");

      // Redirect only on real success.
      navigate("/signup/email-verification", { replace: true });
    } catch (err) {
      setError(err?.message || "Network error while signing up.");
      setAccountExistsLoginUrl("");
      setFirstName("");
      setLastName("");
      setEmailAddress("");
      setCompanyName("");
    } finally {
      // Store email so the resend verification button can call the resend endpoint.
      // We do this regardless of success/failure, as requested.
      if (emailToVerify) sessionStorage.setItem("datahubVerificationEmail", emailToVerify);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 180px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 12px",
        backgroundColor: "#f5f6fa",
      }}
    >
      <div style={{ width: "100%", maxWidth: 680 }}>
        <div className="text-center mb-4">
          <DataHubLogo aria-label="DataHub" style={{ height: 52, width: "auto" }} />
        </div>

        <Card style={{ borderRadius: 12, border: "1px solid #e9ecf2", boxShadow: "0 6px 18px rgba(19,28,69,0.08)" }}>
          <Card.Body style={{ padding: "28px 24px" }}>
            <div className="text-center mb-4">
              <h4 style={{ color: "#131c45", marginBottom: 8 }}>Sign Up for DataHub</h4>
              <p className="text-muted mb-0">
                DataHub brings together the critical datasets that energy companies rely on every day.
              </p>
            </div>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error === "Account Already Exists" ? (
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Account Already Exists</div>
                    <div style={{ marginBottom: 10 }}>
                      It looks like an account with this email is already registered in our system.
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      Please log in instead of creating a new account.{" "}
                      {accountExistsLoginUrl ? (
                        <>
                          Login here:{" "}
                          <a href={accountExistsLoginUrl} target="_blank" rel="noopener noreferrer">
                            {accountExistsLoginUrl}
                          </a>
                          .
                        </>
                      ) : null}
                    </div>
                    <div>
                      If you’ve forgotten your password, you can use the “Forgot Password” option on the login page to reset it.
                    </div>
                  </div>
                ) : (
                  error
                )}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-3">
                {success}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <FormInput
                    type="text"
                    name="firstName"
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                    autoComplete="given-name"
                  />
                </Col>

                <Col md={6}>
                  <FormInput
                    type="text"
                    name="lastName"
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                    autoComplete="family-name"
                  />
                </Col>

                <Col md={6}>
                  <FormInput
                    type="email"
                    name="emailAddress"
                    label="Email Address"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    placeholder="Enter email address"
                    autoComplete="email"
                  />
                </Col>

                <Col md={6}>
                  <FormInput
                    type="text"
                    name="companyName"
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    autoComplete="organization"
                  />
                </Col>
              </Row>

              <div className="mt-4 d-flex justify-content-end">
                <Button type="submit" variant="primary" disabled={loading || !canSubmit}>
                  {loading ? "Registering..." : "Register"}
                </Button>
              </div>

              <div className="text-center mt-3">
                <span className="text-muted">Already have an account? </span>
                {/* Use hard navigation so the app re-initializes the authenticated router */}
                <a
                  href="/"
                  style={{ color: "#131c45", textDecoration: "none" }}
                >
                  Log In
                </a>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
